import { ref, computed, onBeforeUnmount } from 'vue';
import {
  buildSubFactorsBlock,
  consolidateFactor,
  overallReadinessScore,
  factorCoverage,
  weightLabel,
  readinessSummary,
  DEFAULT_READINESS_THRESHOLDS,
} from './docReadiness';

/**
 * JD readiness check runner (PD-1250) — shared between rm-portal and rm-portal-beta.
 *
 * Fires the readiness prompt chain ONCE PER FACTOR (concurrently), covering all of
 * that factor's sub-factors in a single call, then consolidates the raw evidence
 * into a per-factor coverage assessment via the shared docReadiness helpers.
 * Advisory only — never blocks the evaluation flow.
 *
 * The chain receives, per factor:
 *   manual_values: { jd_text, factor_name, factor_description, sub_factors_block }
 *   source_data.custom: { system_reference, factor_id }
 * and returns the `sub_factor_evidence` schema. All chain wiring lives in the DB;
 * this only supplies inputs and reads output.
 *
 * The prompt-chain client and the polling service are INJECTED because they live at
 * portal-local paths (`@/services/promptChainApi`, `@/utils/pollingService`) — the
 * scoring rubric was already shared for the same reason (identical verdicts in both
 * portals), so the runner is shared too rather than duplicated.
 *
 * @param {Object} deps
 * @param {{triggerChain:Function,getExecutionStatus:Function}} deps.promptChainApi
 * @param {{startPolling:Function,stopPolling:Function}}        deps.executionPolling
 */
export function docReadinessRun({ promptChainApi, executionPolling }) {
  const self = {};

  self.error = ref(null);
  self.readinessScore = ref(null); // 0-100, or null until every row has settled
  // One row per factor, seeded up-front so the panel can report progress while the
  // executions land; each row flips from `pending` to its coverage as ITS OWN chain
  // execution returns.
  self.factorResults = ref([]);

  // Non-reactive per-run bookkeeping so a superseded run can't write back.
  let _runToken = 0;
  const _activeExecutionIds = new Set();
  let _thresholds = { ...DEFAULT_READINESS_THRESHOLDS };

  self.hasResult = computed(() => self.factorResults.value.length > 0);
  self.running = computed(() => self.factorResults.value.some((r) => r.status === 'pending'));
  self.summary = computed(() => {
    if (!self.factorResults.value.length) return '';
    if (self.running.value) return 'Checking the document against your framework…';
    return readinessSummary(self.factorResults.value);
  });

  // Reactively replace one factor row (index-addressed) so watchers fire.
  function _patchRow(index, patch) {
    const next = self.factorResults.value.slice();
    if (!next[index]) return;
    next[index] = { ...next[index], ...patch };
    self.factorResults.value = next;
  }

  function _collectOutputs(execution) {
    const allOutputs = {};
    if (execution.steps) {
      execution.steps.forEach((step) => {
        if (step.outputs) {
          for (const [key, value] of Object.entries(step.outputs)) {
            if (typeof value === 'string') {
              try {
                allOutputs[key] = JSON.parse(value);
              } catch {
                allOutputs[key] = value;
              }
            } else {
              allOutputs[key] = value;
            }
          }
        }
      });
    }
    return allOutputs;
  }

  // Pull the { sub_factor_evidence: [...] } object out of the chain outputs,
  // tolerating string/object nesting the same way the AI-assist panel does.
  function _extractResponse(outputs) {
    if (!outputs) return null;
    let result = outputs.result;
    if (result == null) return null;
    if (typeof result === 'string') {
      try {
        result = JSON.parse(result);
      } catch {
        return null;
      }
    }
    // Chains sometimes wrap the payload under a further `.result`.
    if (result && result.sub_factor_evidence) return result;
    if (result && result.result && result.result.sub_factor_evidence) return result.result;
    return null;
  }

  // Trigger + poll one factor's chain execution. Resolves with
  // { response, executionId } — response is the parsed `sub_factor_evidence`, or
  // null on any failure (treated as errored downstream so a broken call never reads
  // as "no evidence found"). `onTriggered(executionId)` fires as soon as the id is
  // known, so the row can bind to it immediately.
  function _runFactor(factor, { chainId, systemReference, getText, onTriggered }) {
    return new Promise((resolve) => {
      const text = getText ? getText() : null;
      if (!text) {
        resolve({ response: null, executionId: null });
        return;
      }

      const payload = {
        prompt_chain_id: chainId,
        source_data: {
          custom: { system_reference: systemReference, factor_id: factor.id },
        },
        manual_values: {
          jd_text: text,
          factor_name: factor.name,
          factor_description: factor.description || '',
          sub_factors_block: buildSubFactorsBlock(factor.subFactors),
        },
      };

      promptChainApi
        .triggerChain(payload)
        .then((resp) => {
          if (!resp.data || !resp.data.success) {
            resolve({ response: null, executionId: null });
            return;
          }
          const executionId = resp.data.execution_id;
          _activeExecutionIds.add(executionId);
          if (onTriggered) onTriggered(executionId);

          executionPolling.startPolling(
            executionId,
            async () => {
              const statusResp = await promptChainApi.getExecutionStatus(executionId);
              if (!statusResp.data || !statusResp.data.success) return {};
              const execution = statusResp.data.data;
              switch (execution.execution_status) {
                case 'queued':
                case 'running':
                  return {};
                case 'completed':
                  return { shouldStop: true, data: _extractResponse(_collectOutputs(execution)) };
                case 'failed':
                  return { shouldStop: true, data: null };
                default:
                  return {};
              }
            },
            (data) => {
              _activeExecutionIds.delete(executionId);
              resolve({ response: data ?? null, executionId });
            },
            () => {
              _activeExecutionIds.delete(executionId);
              resolve({ response: null, executionId });
            },
          );
        })
        .catch(() => resolve({ response: null, executionId: null }));
    });
  }

  /**
   * Run the readiness check for every factor concurrently.
   * @param {Object}   opts
   * @param {Function} opts.getText          returns the JD source text (uploaded file text OR system-doc text)
   * @param {Array}    opts.factors          framework factors ({ id, name, description, weighting, subFactors[] })
   * @param {number}   opts.chainId          readiness prompt-chain id (feature is off when falsy)
   * @param {string}   opts.systemReference  doc.system_number, for execution lookup/audit
   * @param {Object}   [opts.thresholds]     coverage band cutoffs ({ green_min })
   */
  self.run = async ({ getText, factors, chainId, systemReference, thresholds } = {}) => {
    if (!chainId || !factors || !factors.length) return;
    const text = getText ? getText() : null;
    if (!text) return; // text not ready yet — caller re-fires when it is

    const token = ++_runToken;
    self.error.value = null;
    self.readinessScore.value = null;
    _thresholds = { ...DEFAULT_READINESS_THRESHOLDS, ...(thresholds || {}) };

    // Seed a pending row per factor so the panel knows the full set up-front and can
    // count the groups off as their executions return. `description` rides along for
    // the panel's per-factor definition popover.
    self.factorResults.value = factors.map((f) => ({
      id: f.id,
      name: f.name,
      description: f.description || '',
      weighting: Number(f.weighting) || 0,
      weightLabel: weightLabel(f.weighting),
      status: 'pending',
      evidence_quality: null,
      coverage: null,
      executionId: null,
      sub_factors: [],
    }));

    // Fire every factor concurrently; each patches its own row as it resolves,
    // independently of the others.
    await Promise.all(
      factors.map((f, i) =>
        _runFactor(f, {
          chainId,
          systemReference,
          getText,
          onTriggered: (executionId) => {
            if (token === _runToken) _patchRow(i, { executionId });
          },
        }).then(({ response, executionId }) => {
          if (token !== _runToken) return; // superseded by a newer run
          const rollup = consolidateFactor(f.subFactors || [], response);
          _patchRow(i, {
            status: rollup.status,
            evidence_quality: rollup.evidence_quality,
            coverage: factorCoverage(rollup.evidence_quality, rollup.status, _thresholds),
            executionId,
            sub_factors: rollup.sub_factors,
          });
        }),
      ),
    );

    if (token !== _runToken) return;
    // Every row settled — compute the overall weighted score from the results.
    self.readinessScore.value = overallReadinessScore(
      self.factorResults.value.map((r) => ({ weighting: r.weighting, sub_factors: r.sub_factors })),
    );
  };

  self.reset = () => {
    _runToken++;
    for (const id of _activeExecutionIds) executionPolling.stopPolling(id);
    _activeExecutionIds.clear();
    self.error.value = null;
    self.readinessScore.value = null;
    self.factorResults.value = []; // clearing rows makes `running` computed false
  };

  onBeforeUnmount(() => self.reset());

  return self;
}
